import { type FC } from 'react';
import type { ConversationCardSkeletonProps } from 'src/modules/communications/types/conversation-card.type';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import { ConversationPropertySummarySkeleton } from './property-summary-skeleton';

/**
 * Reserves the same header, content, and footer geometry as ConversationCard.
 *
 * @param props - Optional outer container styling.
 * @returns A non-interactive skeleton with no property-preview requests.
 */
export const ConversationCardSkeleton: FC<ConversationCardSkeletonProps> = ({
  className = '',
}) => (
  <div
    aria-hidden="true"
    className={`[container-type:inline-size] w-full max-w-full ${className}`}
  >
    <div className="min-w-0 !rounded-xl !px-[13px] !py-[11px] border border-neutral-200 bg-white">
      <div className="min-w-0">
        <div className="mb-1.5 h-[35px] min-h-[35px] flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-[7px]">
            <TextSkeleton className="!h-3.5 w-32" />
            <TextSkeleton className="!h-[9px] w-20" />
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <TextSkeleton className="!h-6 !w-2.5 !max-w-[10px] rounded-md" />
          </div>
        </div>

        <div className="min-w-0">
          <div className="mb-2 mt-[3px] h-[17px] min-w-0">
            <div className="flex h-full min-w-0 items-center">
              <TextSkeleton className="!h-3 w-full" />
            </div>
          </div>
          <div className="min-w-0">
            <ConversationPropertySummarySkeleton />
          </div>
        </div>

        <div className="mt-[7px] h-[22px] min-h-[22px] min-w-0 flex items-center gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <TextSkeleton className="h-5 w-20 rounded-full" />
          </div>
          <TextSkeleton className="ml-auto h-3 w-20 shrink-0" />
        </div>
      </div>
    </div>
  </div>
);
